/**
 * The interaction log's data seam, over the GENERATED client.
 *
 * Every route here carries a `response_model`, so orval owns the types and the
 * request shapes and neither can drift from the backend. What this module adds
 * is the two things the generated hooks cannot know: which of the three read
 * routes a given subject uses, and what a write invalidates.
 */
import { useQueryClient } from '@tanstack/react-query';

import {
  useCreateInteractionApiV1MasterDataContactsInteractionsPost,
  useDeleteInteractionApiV1MasterDataContactsInteractionsInteractionIdDelete,
  useListContactInteractionsApiV1MasterDataContactsPeopleContactIdInteractionsGet,
  useListOrganizationInteractionsApiV1MasterDataContactsOrganizationsOrganizationIdInteractionsGet,
  useUpdateInteractionApiV1MasterDataContactsInteractionsInteractionIdPut,
} from '../../../generated/api/contacts-interactions/contacts-interactions';
import { useListDealInteractionsApiV1PipelineDealsDealIdInteractionsGet } from '../../../generated/api/pipeline/pipeline';
import type { Interaction } from '../../../generated/api/models/interaction';
import type { InteractionListResponse } from '../../../generated/api/models/interactionListResponse';
import type { InteractionSubjectType } from '../../../generated/api/models/interactionSubjectType';
import { contactKeys, organizationKeys } from '../../../modules/master_data/contacts/hooks/queryKeys';

// The generated model, re-exported rather than re-spelled: `subject_type` is a
// JSON-schema enum, so its value set is the backend's and a local union here
// would be a hand-written copy that drifts silently.
export type { InteractionSubjectType };

export interface InteractionSubject {
  subjectType: InteractionSubjectType;
  subjectId: string;
}

export interface InteractionPageRequest extends InteractionSubject {
  limit: number;
  offset: number;
}

export interface InteractionPageResult {
  data: InteractionListResponse | undefined;
  total: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * One page of a subject's timeline.
 *
 * All three generated queries are called unconditionally — hooks cannot be
 * called behind a branch — and the two that do not apply are held `enabled:
 * false`, so exactly one request goes out per render.
 *
 * A deal reads through PIPELINE's route rather than this module's: the rows are
 * the same, but the near side is a deal and only pipeline's `can_read_deal` can
 * gate one.
 */
export function useSubjectInteractions({
  subjectType,
  subjectId,
  limit,
  offset,
}: InteractionPageRequest): InteractionPageResult {
  const params = { limit, offset };

  const contactQuery = useListContactInteractionsApiV1MasterDataContactsPeopleContactIdInteractionsGet(
    subjectId,
    params,
    { query: { enabled: subjectType === 'contact' && !!subjectId } },
  );
  const organizationQuery =
    useListOrganizationInteractionsApiV1MasterDataContactsOrganizationsOrganizationIdInteractionsGet(
      subjectId,
      params,
      { query: { enabled: subjectType === 'company' && !!subjectId } },
    );
  const dealQuery = useListDealInteractionsApiV1PipelineDealsDealIdInteractionsGet(
    subjectId,
    params,
    { query: { enabled: subjectType === 'deal' && !!subjectId } },
  );

  const active =
    subjectType === 'contact' ? contactQuery : subjectType === 'company' ? organizationQuery : dealQuery;

  return {
    data: active.data,
    total: active.data?.pagination.total ?? 0,
    isLoading: active.isLoading,
    isError: active.isError,
    refetch: () => {
      void active.refetch();
    },
  };
}

/**
 * Every interaction list this session holds, whichever route fetched it.
 *
 * Keyed on the URL rather than on a hand-kept key registry: an orval query key
 * leads with the request path, so one predicate covers the contact, organization
 * and deal timelines — and a fourth read route added later is covered the day it
 * exists rather than the day somebody remembers to add it here.
 */
function invalidateInteractionLists(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({
    predicate: (query) =>
      typeof query.queryKey[0] === 'string' && query.queryKey[0].includes('/interactions'),
  });
}

/**
 * The record whose recency stamps the write just moved.
 *
 * `InteractionService.stamp_engagement` advances `last_contacted_at` /
 * `last_activity_at` in the same transaction as the row, and both are rendered
 * on the very page the dialog was opened from — so the timeline refreshing while
 * "Last activity" stays yesterday reads as a half-saved write.
 */
function invalidateStampedRecords(
  queryClient: ReturnType<typeof useQueryClient>,
  interaction: Pick<Interaction, 'contact_id' | 'organization_id'>,
): void {
  if (interaction.contact_id) {
    queryClient.invalidateQueries({ queryKey: contactKeys.detail(interaction.contact_id) });
  }
  if (interaction.organization_id) {
    queryClient.invalidateQueries({ queryKey: organizationKeys.detail(interaction.organization_id) });
  }
}

export function useLogInteraction() {
  const queryClient = useQueryClient();
  return useCreateInteractionApiV1MasterDataContactsInteractionsPost({
    mutation: {
      onSuccess: (response) => {
        invalidateInteractionLists(queryClient);
        invalidateStampedRecords(queryClient, response.data);
      },
    },
  });
}

export function useEditInteraction() {
  const queryClient = useQueryClient();
  return useUpdateInteractionApiV1MasterDataContactsInteractionsInteractionIdPut({
    mutation: {
      onSuccess: (response) => {
        invalidateInteractionLists(queryClient);
        invalidateStampedRecords(queryClient, response.data);
      },
    },
  });
}

export function useRemoveInteraction() {
  const queryClient = useQueryClient();
  return useDeleteInteractionApiV1MasterDataContactsInteractionsInteractionIdDelete({
    mutation: {
      // The 204 carries no body, so there is no row left to name the contact or
      // organization whose stamps it counted towards. The stamps are monotonic
      // and a delete never lowers one, so nothing on the record changed.
      onSuccess: () => invalidateInteractionLists(queryClient),
    },
  });
}
